import { useMemo, useState } from 'react';
import { buildAssembly, type AssemblyInstance, type AssemblyPresetContent } from '@scs/domain-engine';
import type { PresetSummaryDto } from '@scs/schemas';
import { Api } from '../lib/api';
import { CATEGORY_LABEL } from './LibraryPanel';
import type { EditorStore } from './store';

const NEW = '__new__';
const FEATURES = ['shelfSets', 'fronts', 'frontReveal', 'drawerSets', 'trims', 'trimJoints', 'legs', 'slope'] as const;

/**
 * "Sačuvaj u katalog": the assembly as it is (panels plus shelves, fronts, drawers, plinth / fillers,
 * legs and slope) becomes a catalog item, in an existing folder or a new one.
 */
export function SaveToCatalog({
  asm,
  presets,
  exec,
  snapshot,
  onClose,
  onSaved,
}: {
  asm: AssemblyInstance;
  presets: PresetSummaryDto[] | null;
  exec: EditorStore['exec'];
  snapshot(): Promise<Blob | null>;
  onClose(): void;
  onSaved(p: PresetSummaryDto): void;
}) {
  const folders = useMemo(() => [...new Set((presets ?? []).map((p) => p.folder).filter(Boolean))].sort((a, b) => a.localeCompare(b)), [presets]);
  const [name, setName] = useState(asm.name.replace(/^[A-Z]+\d+\s*/, '') || asm.name);
  const [folder, setFolder] = useState<string>(folders[0] ?? NEW);
  const [newFolder, setNewFolder] = useState('');
  const [category, setCategory] = useState<PresetSummaryDto['category']>('custom');
  const [scope, setScope] = useState<'private' | 'workspace'>('private');
  const [link, setLink] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const content = useMemo((): AssemblyPresetContent => {
    const features: Record<string, unknown> = {};
    for (const k of FEATURES) if (asm[k] !== undefined) features[k] = structuredClone(asm[k]);
    return {
      schemaVersion: 1,
      kind: 'assembly',
      panels: structuredClone(asm.panels),
      materials: { ...asm.materials },
      notes: [],
      ...(Object.keys(features).length ? { features: features as NonNullable<AssemblyPresetContent['features']> } : {}),
    };
  }, [asm]);
  const errors = useMemo(() => buildAssembly({ ...asm, id: 'test' }).issues.filter((i) => i.severity === 'error').map((i) => i.message), [asm]);
  const folderName = (folder === NEW ? newFolder : folder).trim();
  const blocked = !name.trim() || errors.length > 0;

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      let thumbnailAssetId: string | null = null;
      const blob = await snapshot();
      if (blob) thumbnailAssetId = (await Api.uploadAsset(blob, 'preset.png')).id;
      const saved = await Api.createPreset({ name: name.trim(), category, folder: folderName, scope, description: '', tags: [], content, thumbnailAssetId, message: 'Sačuvan sklop' });
      if (link)
        exec(`Poveži sa katalogom: ${saved.name}`, (d) => {
          const a = d.assemblies.find((x) => x.id === asm.id);
          if (a) a.preset = { presetId: saved.id, versionId: saved.latestVersionId, version: 1 };
        });
      onSaved(saved);
    } catch (e) {
      const err = e as Error & { body?: { errors?: string[] } };
      setError([err.message, ...(err.body?.errors ?? [])].join(' '));
    } finally {
      setBusy(false);
    }
  };

  const extras = [
    asm.shelfSets?.length ? 'police' : '',
    asm.fronts?.length ? 'fronte' : '',
    asm.drawerSets?.length ? 'ladice' : '',
    asm.trims ? 'cokla / blende' : '',
    asm.legs ? 'nogice' : '',
    asm.slope ? 'kosina' : '',
  ].filter(Boolean);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="card modal" role="dialog" aria-modal aria-label="Sačuvaj sklop u katalog" onClick={(e) => e.stopPropagation()}>
        <h2>Sačuvaj sklop u katalog</h2>
        <p className="muted">
          „{asm.name}” — {asm.panels.length} ploča{extras.length ? ` + ${extras.join(', ')}` : ''}. Sklop se čuva tačno ovakav i kasnije ubacuje iz kataloga.
        </p>
        <label className="field">
          <span>Naziv</span>
          <input className="input" autoFocus value={name} maxLength={120} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.stopPropagation()} />
        </label>
        <label className="field">
          <span>Folder</span>
          <select className="input" value={folder} onChange={(e) => setFolder(e.target.value)}>
            {folders.map((f) => (
              <option key={f} value={f}>
                📁 {f}
              </option>
            ))}
            <option value={NEW}>+ Novi folder…</option>
          </select>
        </label>
        {folder === NEW && (
          <label className="field">
            <span>Naziv novog foldera (prazno = bez foldera)</span>
            <input className="input" value={newFolder} maxLength={80} placeholder="npr. Moji sklopovi" onChange={(e) => setNewFolder(e.target.value)} onKeyDown={(e) => e.stopPropagation()} />
          </label>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <label className="field">
            <span>Kategorija</span>
            <select className="input" value={category} onChange={(e) => setCategory(e.target.value as PresetSummaryDto['category'])}>
              {Object.entries(CATEGORY_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Dostupnost</span>
            <select className="input" value={scope} onChange={(e) => setScope(e.target.value as 'private' | 'workspace')}>
              <option value="private">Lični (samo ja)</option>
              <option value="workspace">Radionica (svi članovi)</option>
            </select>
          </label>
        </div>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
          <input type="checkbox" checked={link} onChange={(e) => setLink(e.target.checked)} /> Poveži ovaj sklop sa sačuvanim
        </label>
        {errors.length > 0 && <p className="error-text">Sklop ima greške: {errors.slice(0, 3).join(' ')}</p>}
        {error && <p className="error-text">{error}</p>}
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Otkaži
          </button>
          <button className="btn primary" disabled={blocked || busy} onClick={save}>
            {busy ? 'Snimanje…' : 'Sačuvaj u katalog'}
          </button>
        </div>
      </div>
    </div>
  );
}
